import React, { useState, useEffect } from "react";
import axios from "axios";
import { ArrowLeft, Mail, Shield, AlertTriangle, Check, Loader2, Key } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PasswordReset = ({ onBack, resetToken = null }) => {
  const [step, setStep] = useState(resetToken ? 'reset' : 'request'); // 'request', 'sent', 'reset', 'success'
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');

  useEffect(() => {
    if (resetToken) {
      validateToken();
    }
  }, [resetToken]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/validate-reset-token?token=${resetToken}`);
      if (response.data.valid) {
        setTokenEmail(response.data.email);
        setStep('reset');
      } else {
        setError('El enlace de recuperación ha expirado o no es válido');
        setStep('request');
      }
    } catch (err) {
      setError('Error al validar el enlace');
      setStep('request');
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/auth/request-reset`, { email });
      setStep('sent');
    } catch (err) {
      setError('Error al procesar la solicitud. Inténtalo de nuevo.');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    
    if (newPassword.length < 4) {
      setError('La contraseña debe tener al menos 4 caracteres');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      setStep('success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cambiar la contraseña');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <button onClick={onBack} className="flex items-center gap-2 text-emerald-300 hover:text-emerald-200 mb-8 transition-all">
          <ArrowLeft className="w-5 h-5" /> Volver al login
        </button>
        
        <div className="bg-white/5 backdrop-blur-lg border border-emerald-500/20 rounded-3xl p-8">
          {/* Paso 1: Solicitar recuperación */}
          {step === 'request' && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/30">
                  <Key className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-black">Recuperar Contraseña</h1>
                <p className="text-emerald-300 text-sm mt-2">Te enviaremos instrucciones a tu email</p>
              </div>

              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-emerald-400" /> Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="w-full px-4 py-3 bg-white text-slate-900 border-2 border-emerald-500/30 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 transition-all placeholder-slate-400"
                    data-testid="reset-email"
                    required
                  />
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-xl font-bold text-lg shadow-lg shadow-amber-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mail className="w-5 h-5" />}
                  Enviar instrucciones
                </button>
              </form>
            </>
          )}

          {/* Paso 2: Email enviado */}
          {step === 'sent' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-black mb-4">¡Revisa tu email!</h1>
              <p className="text-emerald-200 mb-6">
                Si existe una cuenta con el email <span className="font-bold text-white">{email}</span>, 
                recibirás instrucciones para restablecer tu contraseña.
              </p>
              <p className="text-emerald-300 text-sm mb-6">
                El enlace expira en 1 hora.
              </p>
              <button
                onClick={onBack}
                className="px-6 py-3 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 rounded-xl font-semibold transition-all"
              >
                Volver al login
              </button>
            </div>
          )}

          {/* Paso 3: Nueva contraseña */}
          {step === 'reset' && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/30">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-black">Nueva Contraseña</h1>
                <p className="text-emerald-300 text-sm mt-2">
                  Para: <span className="font-bold text-white">{tokenEmail}</span>
                </p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold mb-2 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-emerald-400" /> Nueva contraseña
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 bg-white text-slate-900 border-2 border-emerald-500/30 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 transition-all placeholder-slate-400"
                    data-testid="reset-new-password"
                    required
                    minLength={4}
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold mb-2 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-emerald-400" /> Confirmar contraseña
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 bg-white text-slate-900 border-2 border-emerald-500/30 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 transition-all placeholder-slate-400"
                    data-testid="reset-confirm-password"
                    required
                  />
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 rounded-xl font-bold text-lg shadow-lg shadow-emerald-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  Cambiar contraseña
                </button>
              </form>
            </>
          )}

          {/* Paso 4: Éxito */}
          {step === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-black mb-4">¡Contraseña actualizada!</h1>
              <p className="text-emerald-200 mb-6">
                Tu contraseña ha sido cambiada correctamente. Ya puedes iniciar sesión con tu nueva contraseña.
              </p>
              <button
                onClick={onBack}
                className="w-full py-4 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 rounded-xl font-bold text-lg shadow-lg shadow-emerald-500/30 transition-all"
              >
                Ir al login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
